import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../component/header";
import Footer from "../component/Footer";
import BannerImg from "../image/gallaryBanner.png";
import { ArrowLeft, ChevronRight, Loader2, Lock } from "lucide-react";

const API_BASE_URL = "";
const PAGE_SIZE = 6;

function imageUrl(path) {
  if (!path) return BannerImg;
  return path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
}

function GalleryGroupImages() {
  const { gallerySlug = "gallery", galleryId, groupId } = useParams();
  const navigate = useNavigate();
  const sentinelRef = useRef(null);
  const [gallery, setGallery] = useState(null);
  const [group, setGroup] = useState(null);
  const [images, setImages] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [columnCount, setColumnCount] = useState(1);

  const loadImages = useCallback(
    async (nextCursor = 0) => {
      try {
        setLoading(true);
        setError("");
        const response = await fetch(
          `/api/gallery/${galleryId}/groups/${groupId}/images?cursor=${nextCursor}&limit=${PAGE_SIZE}`
        );
        const result = await response.json();
        if (!result.success) throw new Error(result.message || "Unable to load images.");

        setGallery(result.data.gallery);
        setGroup(result.data.group);
        setImages((current) =>
          nextCursor === 0 ? result.data.images || [] : [...current, ...(result.data.images || [])]
        );
        setCursor(result.data.nextCursor);
        setHasMore(Boolean(result.data.hasMore));
      } catch (err) {
        setError(err.message || "Unable to load images.");
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [galleryId, groupId]
  );

  useEffect(() => {
    setImages([]);
    setCursor(0);
    setHasMore(true);
    setInitialLoading(true);
    loadImages(0);
  }, [galleryId, groupId, loadImages]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || initialLoading) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && cursor !== null && !loading) {
          loadImages(cursor);
        }
      },
      { rootMargin: "360px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [cursor, hasMore, initialLoading, loadImages, loading]);

  useEffect(() => {
    const updateColumnCount = () => {
      if (window.innerWidth >= 1180) {
        setColumnCount(3);
      } else if (window.innerWidth >= 768) {
        setColumnCount(2);
      } else {
        setColumnCount(1);
      }
    };

    updateColumnCount();
    window.addEventListener("resize", updateColumnCount);
    return () => window.removeEventListener("resize", updateColumnCount);
  }, []);

  const heroImage = group?.coverImage || gallery?.coverImage || BannerImg;
  const masonryColumns = useMemo(() => {
    const columns = Array.from({ length: columnCount }, () => []);
    images.forEach((image, index) => {
      columns[index % columnCount].push({ image, index });
    });
    return columns;
  }, [columnCount, images]);

  return (
    <div className="min-h-screen bg-[#d7d7d7] text-left">
      <Header />
      <main>
        <section
          className="px-4 pb-14 pt-32 text-white"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(8, 15, 16, 0.86), rgba(8, 15, 16, 0.38)), url(${imageUrl(heroImage)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="mx-auto max-w-7xl">
            <nav className="mb-8 flex flex-wrap items-center gap-2 font-outfit text-sm text-white/75">
              <Link to="/" className="hover:text-[#00F0FB]">Home</Link>
              <ChevronRight size={15} />
              <Link to="/gallery" className="hover:text-[#00F0FB]">Gallery</Link>
              {gallery && (
                <>
                  <ChevronRight size={15} />
                  <Link to={`/gallery/${gallerySlug}/${gallery._id}`} className="hover:text-[#00F0FB]">
                    {gallery.title}
                  </Link>
                </>
              )}
              {group && (
                <>
                  <ChevronRight size={15} />
                  <span className="text-white">{group.title}</span>
                </>
              )}
            </nav>

            <button
              type="button"
              onClick={() => navigate(`/gallery/${gallerySlug}/${galleryId}`)}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 font-outfit text-sm font-semibold text-white hover:border-[#00F0FB] hover:text-[#00F0FB]"
            >
              <ArrowLeft size={16} />
              Back to albums
            </button>
            <p className="font-outfit text-xs uppercase tracking-[0.24em] text-[#00F0FB]">Published Album</p>
            <h1 className="mt-4 max-w-4xl font-cinzel text-4xl leading-tight md:text-6xl">
              {group?.title || "Album Images"}
            </h1>
            <p className="mt-5 max-w-3xl font-outfit text-base leading-relaxed text-white/85 md:text-lg">
              {group?.description || "Scroll to continue through the published images and commentary in this album."}
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10">
          {initialLoading && (
            <div>
              <div className="mb-8 rounded-lg border border-[#c6cdcd] bg-white/75 p-5 shadow-sm">
                <div className="h-4 w-56 animate-pulse rounded-full bg-[#c7d0d0]" />
                <div className="mt-3 h-4 w-80 max-w-full animate-pulse rounded-full bg-[#dde4e4]" />
              </div>
              <div className="masonry-gallery">
                {Array.from({ length: columnCount }, (_, columnIndex) => (
                  <div className="masonry-gallery-column" key={`loading-column-${columnIndex}`}>
                    {[0, 1, 2].map((item) => (
                      <article key={`${columnIndex}-${item}`} className="masonry-gallery-item">
                        <div
                          className="animate-pulse bg-gradient-to-br from-[#c8c8c8] via-[#eeeeee] to-[#d8d8d8]"
                          style={{ height: `${260 + ((columnIndex + item) % 3) * 90}px` }}
                        />
                        <div className="mt-3">
                          <div className="h-4 w-full animate-pulse rounded-full bg-[#c4c4c4]" />
                          <div className="mt-2 h-4 w-3/4 animate-pulse rounded-full bg-[#cfcfcf]" />
                        </div>
                      </article>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!initialLoading && error && (
            <div className="rounded-lg border border-[#dce5e5] bg-white p-8 font-outfit text-[#3b4546] shadow-sm">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#E8FBFD] text-[#008EA8]">
                <Lock size={24} />
              </div>
              <h2 className="font-cinzel text-3xl text-[#111718]">Membership access required</h2>
              <p className="mt-3 max-w-2xl leading-7">
                {error} Upgrade to reveal the images and member commentary in this album.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/membership")}
                  className="rounded-full bg-[#111718] px-5 py-3 text-sm font-bold uppercase text-white transition hover:bg-[#00A6C8]"
                >
                  View membership options
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/gallery")}
                  className="rounded-full border border-[#cfdada] px-5 py-3 text-sm font-bold uppercase text-[#111718] transition hover:border-[#00A6C8]"
                >
                  Back to gallery
                </button>
              </div>
            </div>
          )}

          {!initialLoading && !error && images.length === 0 && (
            <div className="rounded-lg border border-dashed border-[#aeb5b5] bg-white/70 p-10 text-center">
              <h2 className="font-cinzel text-2xl text-[#111]">No published images yet</h2>
              <p className="mt-2 font-outfit text-[#444]">Images added by admin will appear here in this album.</p>
            </div>
          )}

          {!initialLoading && !error && images.length > 0 && (
            <div className="masonry-gallery">
              {masonryColumns.map((column, columnIndex) => (
                <div className="masonry-gallery-column" key={`column-${columnIndex}`}>
                  {column.map(({ image, index }) => (
                    <article
                      key={image._id || `${image.url}-${index}`}
                      className="masonry-gallery-item"
                    >
                      <div className="overflow-hidden bg-[#cfcfcf]">
                        <img
                          src={imageUrl(image.url)}
                          alt={image.caption || group?.title || "Gallery image"}
                          className="block h-auto w-full"
                          loading="lazy"
                        />
                      </div>
                      <div className="mt-3 font-outfit text-[13px] leading-5 text-[#111]">
                        <p>
                          <strong>{image.memberId || "Member ID pending"}</strong>:{" "}
                          {image.feedback || image.caption || "Published commentary will appear here when available."}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div ref={sentinelRef} className="flex min-h-24 items-center justify-center py-6">
            {loading && !initialLoading && (
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-outfit text-sm text-[#111]">
                <Loader2 className="animate-spin" size={16} />
                Loading more images
              </span>
            )}
            {!hasMore && images.length > 0 && (
              <span className="font-outfit text-sm text-[#555]">End of album</span>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <style>{`
        .masonry-gallery {
          display: grid;
          grid-template-columns: repeat(${columnCount}, minmax(0, 1fr));
          gap: 28px;
          align-items: start;
        }

        .masonry-gallery-column {
          display: flex;
          flex-direction: column;
          gap: 28px;
        }

        .masonry-gallery-item {
          width: 100%;
        }
      `}</style>
    </div>
  );
}

export default GalleryGroupImages;
